import type { Channel, Lap, ResampledLap, SessionData } from '@tla/types';

/** Find a channel by its canonical id. */
export function getChannel(session: SessionData, id: string): Channel | undefined {
  return session.channels.find((c) => c.id === id);
}

/**
 * Return a copy of `values` with NaN gaps filled by linear interpolation between
 * the nearest valid neighbours. Leading/trailing NaN runs are held at the first/
 * last valid value. If no valid values exist the array is returned unchanged.
 */
export function interpolateNaN(values: Float64Array): Float64Array {
  const out = Float64Array.from(values);
  const n = out.length;

  let firstValid = -1;
  let lastValid = -1;
  for (let i = 0; i < n; i++) {
    if (Number.isFinite(out[i])) {
      if (firstValid === -1) firstValid = i;
      lastValid = i;
    }
  }
  if (firstValid === -1) return out; // all NaN

  // Hold leading/trailing edges.
  for (let i = 0; i < firstValid; i++) out[i] = out[firstValid];
  for (let i = lastValid + 1; i < n; i++) out[i] = out[lastValid];

  // Linear interpolation across internal gaps.
  let i = firstValid;
  while (i < lastValid) {
    if (Number.isFinite(out[i + 1])) {
      i++;
      continue;
    }
    // Find next valid index j.
    let j = i + 1;
    while (j <= lastValid && !Number.isFinite(out[j])) j++;
    const startVal = out[i];
    const endVal = out[j];
    const span = j - i;
    for (let k = 1; k < span; k++) {
      out[i + k] = startVal + ((endVal - startVal) * k) / span;
    }
    i = j;
  }
  return out;
}

/** Distance travelled at the start of a lap (absolute). */
export function lapStartDistance(session: SessionData, lap: Lap): number {
  return session.distance[lap.startIdx];
}

/** Length of a lap in metres. */
export function lapLength(session: SessionData, lap: Lap): number {
  return session.distance[lap.endIdx] - session.distance[lap.startIdx];
}

/**
 * Resample a lap onto a uniform distance grid of `gridSize` points spanning
 * 0..lapLength. Each requested channel plus an elapsed-time track is linearly
 * interpolated. Distances are relative to the lap start.
 *
 * @param session Parsed session data.
 * @param lap Lap to resample.
 * @param channelIds Channel ids to include.
 * @param gridSize Number of points in the output grid.
 */
export function resampleLap(
  session: SessionData,
  lap: Lap,
  channelIds: string[],
  gridSize = 1000,
): ResampledLap {
  const { startIdx, endIdx } = lap;
  const dStart = session.distance[startIdx];
  const len = session.distance[endIdx] - dStart;
  const tStart = session.time[startIdx];

  const grid = new Float64Array(gridSize);
  for (let k = 0; k < gridSize; k++) {
    grid[k] = gridSize === 1 ? 0 : (len * k) / (gridSize - 1);
  }

  // Prepare source distance (relative) for the lap slice.
  const sliceLen = endIdx - startIdx + 1;
  const srcDist = new Float64Array(sliceLen);
  for (let i = 0; i < sliceLen; i++) {
    srcDist[i] = session.distance[startIdx + i] - dStart;
  }

  const interpOnto = (src: Float64Array): Float64Array => {
    const filled = interpolateNaN(src);
    const res = new Float64Array(gridSize);
    let j = 0;
    for (let k = 0; k < gridSize; k++) {
      const target = grid[k];
      while (j < sliceLen - 2 && srcDist[j + 1] < target) j++;
      const d0 = srcDist[j];
      const d1 = srcDist[j + 1];
      const span = d1 - d0;
      const frac = span > 0 ? (target - d0) / span : 0;
      res[k] = filled[j] + (filled[j + 1] - filled[j]) * frac;
    }
    return res;
  };

  // Elapsed time relative to lap start.
  const srcTime = new Float64Array(sliceLen);
  for (let i = 0; i < sliceLen; i++) {
    srcTime[i] = session.time[startIdx + i] - tStart;
  }
  const time = interpOnto(srcTime);

  const channels: Record<string, Float64Array> = {};
  for (const id of channelIds) {
    const ch = getChannel(session, id);
    if (!ch) continue;
    const src = ch.samples.subarray(startIdx, endIdx + 1);
    channels[id] = interpOnto(Float64Array.from(src));
  }

  return {
    sessionId: '',
    lapNumber: lap.number,
    distance: grid,
    channels,
    time,
  };
}
